/**
 * Lookup Settings API
 *
 * API client functions for managing lookup entities:
 * Job Titles, Disciplines, Skills, and Leave Types.
 */

import { apiClient } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface JobTitleItem {
  id: number;
  job_title: string;
}

export interface JobTitlesListResponse {
  message: string;
  values: JobTitleItem[];
  count: number;
}

export interface DisciplineItem {
  id: number;
  discipline: string;
  code?: string;
}

export interface DisciplinesListResponse {
  message: string;
  values: DisciplineItem[];
  count: number;
}

export interface SkillItem {
  id: number;
  name: string;
  discipline_id: number | null;
  discipline_name: string | null;
  is_delete: boolean | null;
}

export interface SkillsListResponse {
  message: string;
  values: SkillItem[];
  count: number;
}

export interface LeaveTypeItem {
  id: number;
  description: string;
  is_delete: boolean | null;
  created_by_username: string | null;
  updated_by_username: string | null;
}

export interface LeaveTypesListResponse {
  message: string;
  status: boolean;
  count: number;
  values: LeaveTypeItem[];
}

// ---------------------------------------------------------------------------
// Job Titles
// ---------------------------------------------------------------------------

export async function getJobTitles(search?: string): Promise<JobTitlesListResponse> {
  const params = new URLSearchParams({ limit: '1000', skip: '0' });
  if (search) params.set('search', search);
  return apiClient.get<JobTitlesListResponse>(`/jobtitle/autocomplete?${params.toString()}`);
}

export async function createJobTitle(
  jobTitle: string,
): Promise<{ message: string; job_title_id: number }> {
  return apiClient.post<{ message: string; job_title_id: number }>('/jobtitle/create', {
    job_title: jobTitle,
  });
}

export async function updateJobTitle(
  id: number,
  jobTitle: string,
): Promise<{ message: string; job_title_id: number }> {
  return apiClient.put<{ message: string; job_title_id: number }>('/jobtitle/update', {
    id,
    job_title: jobTitle,
  });
}

export async function deleteJobTitle(id: number): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(`/jobtitle/${id}`);
}

// ---------------------------------------------------------------------------
// Disciplines
// ---------------------------------------------------------------------------

export async function getDisciplines(search?: string): Promise<DisciplinesListResponse> {
  const params = new URLSearchParams({ limit: '1000', skip: '0' });
  if (search) params.set('search', search);
  return apiClient.get<DisciplinesListResponse>(`/discipline/autocomplete?${params.toString()}`);
}

export async function createDiscipline(body: { discipline: string; code?: string }): Promise<{
  message: string;
}> {
  return apiClient.post<{ message: string }>('/lookup/discipline/add', body);
}

export async function updateDiscipline(
  id: number,
  body: { discipline: string; code?: string },
): Promise<{ message: string }> {
  return apiClient.put<{ message: string }>(`/lookup/discipline/update/${id}`, body);
}

export async function deleteDiscipline(id: number): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(`/lookup/discipline/delete/${id}`);
}

// ---------------------------------------------------------------------------
// Skills
// ---------------------------------------------------------------------------

export async function getSkills(disciplineId?: number): Promise<SkillsListResponse> {
  const params = new URLSearchParams({ limit: '1000', skip: '0' });
  if (disciplineId) params.set('discipline_id', String(disciplineId));
  return apiClient.get<SkillsListResponse>(`/skill?${params.toString()}`);
}

export async function createSkill(body: { name: string; discipline_id?: number }): Promise<{
  message: string;
  values: number;
}> {
  return apiClient.post<{ message: string; values: number }>('/skill/create', body);
}

export async function updateSkill(body: {
  id: number;
  name: string;
  discipline_id?: number;
}): Promise<{ message: string; values: number }> {
  return apiClient.put<{ message: string; values: number }>('/skill/update', body);
}

export async function deleteSkill(id: number): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(`/skill/${id}`);
}

// ---------------------------------------------------------------------------
// Leave Types
// ---------------------------------------------------------------------------

export async function getLeaveTypes(search?: string): Promise<LeaveTypesListResponse> {
  const params = new URLSearchParams({ limit: '1000', skip: '0' });
  if (search) params.set('search', search);
  return apiClient.get<LeaveTypesListResponse>(`/leave-type/list?${params.toString()}`);
}

export async function createLeaveType(
  description: string,
): Promise<{ message: string; status: boolean; values: number }> {
  return apiClient.post<{ message: string; status: boolean; values: number }>('/leave-type/add', {
    description,
  });
}

export async function updateLeaveType(
  id: number,
  description: string,
): Promise<{ message: string; status: boolean }> {
  return apiClient.post<{ message: string; status: boolean }>(`/leave-type/update/${id}`, {
    description,
  });
}

export async function deleteLeaveType(id: number): Promise<{ message: string; status: boolean }> {
  return apiClient.delete<{ message: string; status: boolean }>(`/leave-type/delete/${id}`);
}
